// src/components/SmartAssistantWidget.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaRobot,
  FaHeadset,
  FaTimes,
  FaSearch,
  FaMapMarkerAlt,
  FaStar,
  FaFire,
  FaHotel,
  FaPlane,
  FaUser,
  FaPhoneAlt,
  FaMicrophone,
  FaVolumeUp,
  FaVolumeMute,
  FaSpinner,
  FaCheckCircle,
  FaPhoneSlash,
  FaTicketAlt,
  FaClock,
  FaPaperPlane,
  FaFileAlt,
  FaBusinessTime,
  FaStop,
  FaReply
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ForfyCommerceCockpit, { ForfyCommerceContext } from './business/ForfyCommerceCockpit';
import ForfyCommerceRuntimeBoundary from './business/ForfyCommerceRuntimeBoundary';
import ForfyMerchantPanel from './business/forfy/ForfyMerchantPanel';

// Import des services
import SpeechRecognitionService from '../services/SpeechRecognitionService';
import TicketService, { Ticket } from '../services/TicketService';

// ✅ INTÉGRATION VAPI POUR L'AGENT VOCAL
import Vapi from '@vapi-ai/web';
// Clé publique Vapi insérée directement (100% sécurisé pour une Public Key)
const vapi = new Vapi('a3f60f73-8199-4cc0-958b-1517686930c8');

// ✅ Lire VITE_SUPPORT_ALWAYS_OPEN (au lieu de hardcoder)
const envBool = (key: string, def: boolean) => {
  const raw = (import.meta as any)?.env?.[key];
  if (raw == null) return def;
  const v = String(raw).toLowerCase().trim();
  return ['1', 'true', 'yes', 'on'].includes(v);
};

// ✅ FORCER LE SUPPORT EN 24/7 via Render: VITE_SUPPORT_ALWAYS_OPEN=true
const FORCE_SUPPORT_247 = envBool('VITE_SUPPORT_ALWAYS_OPEN', true);

// Types pour les modes du widget
type AssistantMode = 'closed' | 'forfy' | 'support' | 'tickets' | 'commerce';
type CallView = 'menu' | 'calling' | 'connected' | 'voicemail';

interface ChatMessage {
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  type?: 'text' | 'voice';
}

const SmartAssistantWidget: React.FC = () => {
  const [activeMode, setActiveMode] = useState<AssistantMode>('closed');
  const [callView, setCallView] = useState<CallView>('menu');
  const [message, setMessage] = useState('');
  const [commerceContext, setCommerceContext] = useState<ForfyCommerceContext | null>(null);
  const [commerceAssistantHandoff, setCommerceAssistantHandoff] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      sender: 'assistant',
      text: "👋 Bonjour! Je suis Forfy, votre assistant virtuel. Comment puis-je vous aider aujourd'hui?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [callDuration, setCallDuration] = useState(0); 
  const [isMuted, setIsMuted] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'general' as const,
    priority: 'medium' as const
  });
  const [supportStatus, setSupportStatus] = useState({
    isOpen: true,
    waitTime: '',
    nextOpening: ''
  });

  const chatEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const timerRef = useRef<number | null>(null);


  useEffect(() => {
    const openFromHeader = (event: Event) => {
      const detail = (event as CustomEvent<ForfyCommerceContext & { mode?: AssistantMode }>).detail;

      if (detail?.source === 'commerce-dashboard' || detail?.mode === 'commerce') {
        setCommerceContext({
          source: detail.source || 'commerce-dashboard',
          businessId: detail.businessId || null,
          businessName: detail.businessName || null,
          businessPlan: detail.businessPlan || null,
          activeTab: detail.activeTab || null,
          activeNav: detail.activeNav || null,
          userRole: detail.userRole || null,
          merchantContext: (detail as any).merchantContext || null,
        });
        setCommerceAssistantHandoff(false);
        setActiveMode('commerce');
        return;
      }

      const nextMode = detail?.mode && detail.mode !== 'closed' ? detail.mode : 'support';
      setCommerceAssistantHandoff(false);
      setActiveMode(nextMode);
    };

    window.addEventListener('forfy:open', openFromHeader as EventListener);
    return () => window.removeEventListener('forfy:open', openFromHeader as EventListener);
  }, []);

  // FORFEO_COMMERCE_V22N2_DICTATION_LOCK
  // FORFEO_COMMERCE_V22N4_VOICE_BRIDGE_HARD_LOCK
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    let recognition: any = null;
    let activeButton: HTMLElement | null = null;
    let activeField: HTMLTextAreaElement | HTMLInputElement | null = null;

    const getSpeechRecognition = () => {
      return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
    };

    const ensureVoiceToast = () => {
      let toast = document.getElementById('forfy-voice-bridge-toast');

      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'forfy-voice-bridge-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(toast);
      }

      return toast;
    };

    const announceVoiceStatus = (message: string, tone: 'idle' | 'listening' | 'error' | 'success' = 'idle') => {
      const toast = ensureVoiceToast();
      toast.textContent = message;
      toast.dataset.tone = tone;
      toast.dataset.visible = 'true';

      window.clearTimeout((toast as any).__forfyVoiceTimer);
      (toast as any).__forfyVoiceTimer = window.setTimeout(() => {
        toast.dataset.visible = 'false';
      }, tone === 'listening' ? 9000 : 3200);
    };

    const isVisibleTextField = (field: Element | null): field is HTMLTextAreaElement | HTMLInputElement => {
      if (!(field instanceof HTMLTextAreaElement) && !(field instanceof HTMLInputElement)) {
        return false;
      }

      const box = field.getBoundingClientRect();

      return box.width > 20 && box.height > 12 && !field.disabled && !field.readOnly;
    };

    const scoreField = (field: HTMLTextAreaElement | HTMLInputElement) => {
      const box = field.getBoundingClientRect();
      const placeholder = `${field.getAttribute('placeholder') || ''} ${field.getAttribute('aria-label') || ''}`.toLowerCase();
      const visibleOrder = Math.max(0, window.innerHeight - Math.abs(box.top));

      let score = box.width + box.height + visibleOrder;

      if (document.activeElement === field) score += 10000;
      if (field instanceof HTMLTextAreaElement) score += 1800;
      if (placeholder.includes('forfy')) score += 3500;
      if (placeholder.includes('brouillon')) score += 3200;
      if (placeholder.includes('valide')) score += 2200;
      if (placeholder.includes('prompt')) score += 1800;
      if (placeholder.includes('assistant')) score += 1400;
      if (box.top >= 0 && box.top <= window.innerHeight) score += 900;

      return score;
    };

    const findAssistantInput = (button?: HTMLElement | null): HTMLTextAreaElement | HTMLInputElement | null => {
      const focused = document.activeElement;

      if (isVisibleTextField(focused)) {
        return focused;
      }

      const shell =
        button?.closest('[class*="forfy"], [class*="assistant"], [class*="support"], [aria-label*="Forfy"], [aria-label*="assistant"]') ||
        document;

      const selectors = [
        'textarea[aria-label*="Forfy"]',
        'textarea[aria-label*="Prompt"]',
        'textarea[placeholder*="Forfy"]',
        'textarea[placeholder*="brouillon"]',
        'textarea[placeholder*="Valide"]',
        'textarea[placeholder*="assistant"]',
        'textarea',
        'input[type="text"]',
        'input:not([type])',
      ].join(',');

      const scopedFields = Array.from(
        shell.querySelectorAll<HTMLTextAreaElement | HTMLInputElement>(selectors),
      ).filter(isVisibleTextField);

      const globalFields = Array.from(
        document.querySelectorAll<HTMLTextAreaElement | HTMLInputElement>(selectors),
      ).filter(isVisibleTextField);

      const fields = scopedFields.length ? scopedFields : globalFields;

      return fields.sort((a, b) => scoreField(b) - scoreField(a))[0] || null;
    };

    const writeTranscript = (field: HTMLTextAreaElement | HTMLInputElement, transcript: string) => {
      const cleanTranscript = transcript.trim();

      if (!cleanTranscript) {
        announceVoiceStatus('Aucun texte détecté. Réessaie en parlant plus près du micro.', 'error');
        return;
      }

      const currentValue = field.value || '';
      const start = typeof field.selectionStart === 'number' ? field.selectionStart : currentValue.length;
      const end = typeof field.selectionEnd === 'number' ? field.selectionEnd : currentValue.length;
      const prefix = currentValue.slice(0, start);
      const suffix = currentValue.slice(end);
      const spacer = prefix && !prefix.endsWith(' ') && !prefix.endsWith('\n') ? ' ' : '';
      const nextValue = `${prefix}${spacer}${cleanTranscript}${suffix}`;

      const descriptor =
        Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value') ||
        Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value') ||
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

      if (descriptor?.set) {
        descriptor.set.call(field, nextValue);
      } else {
        field.value = nextValue;
      }

      try {
        field.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: cleanTranscript }));
      } catch {
        field.dispatchEvent(new Event('input', { bubbles: true }));
      }

      field.dispatchEvent(new Event('change', { bubbles: true }));
      field.focus();

      const cursor = Math.min(nextValue.length, prefix.length + spacer.length + cleanTranscript.length);

      try {
        field.setSelectionRange(cursor, cursor);
      } catch {
        // ignore unsupported selection
      }

      announceVoiceStatus('Dictée ajoutée dans le brouillon Forfy.', 'success');
    };

    const setButtonListening = (button: HTMLElement | null, listening: boolean) => {
      if (!button) {
        return;
      }

      button.dataset.forfyDictating = listening ? 'true' : 'false';
      button.setAttribute('aria-pressed', listening ? 'true' : 'false');

      if (listening) {
        button.setAttribute('data-forfy-voice-active', 'true');
      } else {
        button.removeAttribute('data-forfy-voice-active');
      }
    };

    const stopCurrentRecognition = () => {
      try {
        recognition?.stop?.();
      } catch {
        // ignore browser stop errors
      }

      setButtonListening(activeButton, false);
      recognition = null;
      activeButton = null;
      activeField = null;
    };

    const isDictationButton = (button: HTMLElement) => {
      const textParts = [
        button.getAttribute('aria-label') || '',
        button.getAttribute('title') || '',
        button.textContent || '',
        button.getAttribute('data-testid') || '',
        button.className?.toString?.() || '',
        button.parentElement?.textContent || '',
      ];

      const label = textParts.join(' ').toLowerCase();

      const hasVoiceWords =
        label.includes('dicter') ||
        label.includes('dictée') ||
        label.includes('dictation') ||
        label.includes('micro') ||
        label.includes('microphone') ||
        label.includes('vocal') ||
        label.includes('voix') ||
        label.includes('voice') ||
        label.includes('speech');

      const nearAssistant =
        Boolean(button.closest('[class*="forfy"]')) ||
        Boolean(button.closest('[class*="assistant"]')) ||
        Boolean(button.closest('[class*="support"]')) ||
        Boolean(button.closest('[aria-label*="Forfy"]')) ||
        Boolean(button.closest('[aria-label*="assistant"]'));

      const iconOnlyNearSend =
        Boolean(button.querySelector('svg')) &&
        !button.textContent?.trim() &&
        Boolean(button.parentElement?.textContent?.toLowerCase().includes('envoyer'));

      return nearAssistant && (hasVoiceWords || iconOnlyNearSend);
    };

    const startRecognitionForButton = (button: HTMLElement, event: Event) => {
      const SpeechRecognition = getSpeechRecognition();
      const field = findAssistantInput(button);

      event.preventDefault();
      event.stopPropagation();

      if (!field) {
        announceVoiceStatus('Aucun champ Forfy lisible trouvé pour recevoir la dictée.', 'error');
        return;
      }

      field.focus();

      if (!SpeechRecognition) {
        announceVoiceStatus('La dictée navigateur n’est pas disponible ici. Utilise Chrome ou Edge et autorise le micro.', 'error');
        field.placeholder = 'Dictée non disponible dans ce navigateur. Utilise Chrome ou Edge.';
        return;
      }

      if (recognition) {
        stopCurrentRecognition();
        announceVoiceStatus('Dictée arrêtée.', 'idle');
        return;
      }

      recognition = new SpeechRecognition();
      activeButton = button;
      activeField = field;

      recognition.lang = 'fr-CA';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setButtonListening(activeButton, true);
        announceVoiceStatus('Écoute en cours… parle maintenant.', 'listening');
      };

      recognition.onresult = (speechEvent: any) => {
        let finalTranscript = '';

        for (let index = speechEvent.resultIndex || 0; index < speechEvent.results.length; index += 1) {
          const result = speechEvent.results[index];
          const transcript = result?.[0]?.transcript || '';

          if (result?.isFinal) {
            finalTranscript += transcript;
          }
        }

        if (finalTranscript && activeField) {
          writeTranscript(activeField, finalTranscript);
        }
      };

      recognition.onerror = (speechEvent: any) => {
        const errorCode = speechEvent?.error || 'unknown';

        console.warn('[Forfy Commerce voice bridge]', errorCode, speechEvent);

        const messages: Record<string, string> = {
          'not-allowed': 'Micro bloqué. Autorise le micro dans le navigateur, puis réessaie.',
          'service-not-allowed': 'Service de dictée bloqué par le navigateur.',
          'no-speech': 'Aucune voix détectée. Réessaie en parlant plus près du micro.',
          'audio-capture': 'Aucun micro détecté par le navigateur.',
          network: 'Erreur réseau du service de dictée navigateur.',
          aborted: 'Dictée annulée.',
        };

        announceVoiceStatus(messages[errorCode] || 'Dictée interrompue. Vérifie le micro et réessaie.', 'error');

        if (activeField) {
          activeField.focus();
        }
      };

      recognition.onend = () => {
        setButtonListening(activeButton, false);
        recognition = null;
        activeButton = null;
        activeField = null;
      };

      try {
        recognition.start();
      } catch (error) {
        console.warn('[Forfy Commerce voice bridge] start failed', error);
        setButtonListening(activeButton, false);
        recognition = null;
        activeButton = null;
        activeField = null;
        announceVoiceStatus('Impossible de démarrer la dictée. Réessaie après avoir autorisé le micro.', 'error');
      }
    };

    const handleForfyDictationClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const button = target?.closest('button, [role="button"]') as HTMLElement | null;

      if (!button || !isDictationButton(button)) {
        return;
      }

      startRecognitionForButton(button, event);
    };

    document.addEventListener('click', handleForfyDictationClick, true);

    return () => {
      document.removeEventListener('click', handleForfyDictationClick, true);
      stopCurrentRecognition();

      const toast = document.getElementById('forfy-voice-bridge-toast');
      if (toast) {
        toast.remove();
      }
    };
  }, []);

  // FORFEO_COMMERCE_V22N8_MOBILE_FORFY_LAUNCHER_LOCK
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    let lastOpenAt = 0;
    let launcherButton: HTMLButtonElement | null = null;

    const isBusinessDashboard = () =>
      window.location.pathname.includes('business-dashboard') ||
      window.location.href.includes('/business-dashboard');

    const isMobileViewport = () => {
      try {
        return window.matchMedia('(max-width: 860px), (pointer: coarse)').matches;
      } catch {
        return window.innerWidth <= 860;
      }
    };

    const openForfyCommerceMobile = (event?: Event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const now = Date.now();
      if (now - lastOpenAt < 550) {
        return;
      }

      lastOpenAt = now;

      window.dispatchEvent(
        new CustomEvent('forfy:open', {
          detail: {
            mode: 'commerce',
            source: 'mobile-forfy-launcher-lock',
            context: {
              surface: 'business-dashboard-mobile',
              requiresHumanValidation: true,
            },
          },
        }),
      );

      setActiveMode('commerce' as any);
    };

    const applyLauncherStyles = (button: HTMLButtonElement) => {
      button.style.position = 'fixed';
      button.style.right = '18px';
      button.style.bottom = 'calc(18px + env(safe-area-inset-bottom, 0px))';
      button.style.zIndex = '2147482999';
      button.style.minWidth = '58px';
      button.style.height = '58px';
      button.style.padding = '0 16px';
      button.style.border = '1px solid rgba(255, 255, 255, 0.42)';
      button.style.borderRadius = '999px';
      button.style.background = 'linear-gradient(135deg, #0b3b8f, #114bd6)';
      button.style.color = '#ffffff';
      button.style.boxShadow = '0 18px 42px rgba(15, 23, 42, 0.28)';
      button.style.fontWeight = '900';
      button.style.fontSize = '13px';
      button.style.letterSpacing = '-0.01em';
      button.style.display = isBusinessDashboard() && isMobileViewport() ? 'inline-flex' : 'none';
      button.style.alignItems = 'center';
      button.style.justifyContent = 'center';
      button.style.gap = '8px';
      button.style.cursor = 'pointer';
      button.style.touchAction = 'manipulation';
      button.style.webkitTapHighlightColor = 'transparent';
    };

    const ensureMobileLauncher = () => {
      const existing = document.getElementById('forfy-commerce-mobile-launcher') as HTMLButtonElement | null;

      if (existing) {
        launcherButton = existing;
      }

      if (!launcherButton) {
        launcherButton = document.createElement('button');
        launcherButton.id = 'forfy-commerce-mobile-launcher';
        launcherButton.type = 'button';
        launcherButton.setAttribute('aria-label', 'Ouvrir Forfy Commerce');
        launcherButton.setAttribute('data-forfy-commerce-mobile-launcher', 'v22n8');
        launcherButton.innerHTML = '<span aria-hidden="true">🤖</span><span>Forfy</span>';
        launcherButton.addEventListener('click', openForfyCommerceMobile);
        launcherButton.addEventListener('pointerup', openForfyCommerceMobile);
        launcherButton.addEventListener('touchend', openForfyCommerceMobile);
        document.body.appendChild(launcherButton);
      }

      applyLauncherStyles(launcherButton);
    };

    const removeMobileLauncher = () => {
      if (launcherButton) {
        launcherButton.removeEventListener('click', openForfyCommerceMobile);
        launcherButton.removeEventListener('pointerup', openForfyCommerceMobile);
        launcherButton.removeEventListener('touchend', openForfyCommerceMobile);
        launcherButton.remove();
        launcherButton = null;
      }
    };

    const getLauncherCandidate = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof HTMLElement)) {
        return null;
      }

      return target.closest(
        [
          '#forfy-commerce-mobile-launcher',
          '[data-forfy-commerce-mobile-launcher]',
          '[data-forfy-commerce-launcher]',
          '[aria-label*="Forfy Commerce"]',
          '[title*="Forfy Commerce"]',
          'button',
          'a',
          '[role="button"]',
        ].join(','),
      ) as HTMLElement | null;
    };

    const looksLikeForfyCommerceLauncher = (element: HTMLElement) => {
      if (element.id === 'forfy-commerce-mobile-launcher') {
        return true;
      }

      if (element.getAttribute('data-forfy-commerce-mobile-launcher')) {
        return true;
      }

      if (element.getAttribute('data-forfy-commerce-launcher')) {
        return true;
      }

      const label = [
        element.getAttribute('aria-label') || '',
        element.getAttribute('title') || '',
        element.getAttribute('data-testid') || '',
        element.getAttribute('data-test') || '',
        element.className?.toString?.() || '',
        element.textContent || '',
      ]
        .join(' ')
        .toLowerCase();

      const hasForfy = label.includes('forfy');
      const hasCommerce = label.includes('commerce') || label.includes('marchand') || label.includes('assistant');

      return hasForfy && hasCommerce;
    };

    const isInsideOpenAssistantSurface = (element: HTMLElement) =>
      Boolean(
        element.closest(
          [
            '.forfy-commerce-cockpit',
            '.forfy-commerce-offer-assistant',
            '.forfy-commerce-runtime-shield',
            '[data-forfy-offer-assistant]',
            '[data-forfy-marketing-copy-pack]',
            '[data-forfy-launch-planner]',
          ].join(','),
        ),
      );

    const handleMobileForfyTap = (event: Event) => {
      if (!isBusinessDashboard() || !isMobileViewport()) {
        return;
      }

      const candidate = getLauncherCandidate(event.target);

      if (!candidate) {
        return;
      }

      if (candidate.id !== 'forfy-commerce-mobile-launcher' && isInsideOpenAssistantSurface(candidate)) {
        return;
      }

      if (!looksLikeForfyCommerceLauncher(candidate)) {
        return;
      }

      openForfyCommerceMobile(event);
    };

    const refreshLauncher = () => {
      if (isBusinessDashboard() && isMobileViewport()) {
        ensureMobileLauncher();
      } else if (launcherButton) {
        applyLauncherStyles(launcherButton);
      }
    };

    ensureMobileLauncher();

    document.addEventListener('click', handleMobileForfyTap, true);
    document.addEventListener('pointerup', handleMobileForfyTap, true);
    document.addEventListener('touchend', handleMobileForfyTap, true);
    window.addEventListener('resize', refreshLauncher);
    window.addEventListener('orientationchange', refreshLauncher);
    window.addEventListener('popstate', refreshLauncher);

    const refreshTimer = window.setInterval(refreshLauncher, 1500);

    return () => {
      document.removeEventListener('click', handleMobileForfyTap, true);
      document.removeEventListener('pointerup', handleMobileForfyTap, true);
      document.removeEventListener('touchend', handleMobileForfyTap, true);
      window.removeEventListener('resize', refreshLauncher);
      window.removeEventListener('orientationchange', refreshLauncher);
      window.removeEventListener('popstate', refreshLauncher);
      window.clearInterval(refreshTimer);
      removeMobileLauncher();
    };
  }, []);

  // FORFEO_COMMERCE_V22N10_OFFER_HANDOFF_RECEIVER
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    type OfferHandoffPacketLite = {
      schemaVersion?: string;
      title?: string;
      category?: string;
      readinessScore?: number;
      readinessLabel?: string;
      offerSheet?: string;
      marketingPackText?: string;
      launchPlanText?: string;
      preparedAt?: string;
    };

    const storageKey = 'forfy-commerce-offer-handoff-draft';
    let receiver: HTMLDivElement | null = null;

    const isBusinessDashboard = () =>
      window.location.pathname.includes('business-dashboard') ||
      window.location.href.includes('/business-dashboard');

    const readPacket = (): OfferHandoffPacketLite | null => {
      try {
        const raw = window.localStorage?.getItem(storageKey);

        if (!raw) {
          return null;
        }

        const parsed = JSON.parse(raw);

        if (!parsed || typeof parsed !== 'object') {
          return null;
        }

        return parsed as OfferHandoffPacketLite;
      } catch (error) {
        console.warn('[Forfy Commerce] offer handoff read skipped', error);
        return null;
      }
    };

    const copyText = async (value: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          return;
        }
      } catch {
        // fallback below
      }

      const area = document.createElement('textarea');
      area.value = value;
      area.setAttribute('readonly', 'true');
      area.style.position = 'fixed';
      area.style.left = '-9999px';
      document.body.appendChild(area);
      area.select();

      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(area);
      }
    };

    const buildSummary = (packet: OfferHandoffPacketLite) =>
      [
        'DOSSIER FORFY PRÊT POUR OFFRES',
        '',
        `Titre: ${packet.title || 'Nouvelle offre Forfeo'}`,
        `Type: ${packet.category || 'Offre'}`,
        `Score: ${packet.readinessScore ?? '—'}% — ${packet.readinessLabel || 'À valider'}`,
        packet.preparedAt ? `Préparé le: ${packet.preparedAt}` : '',
        '',
        'Statut: brouillon local seulement.',
        'Aucune publication automatique. Aucun prix, paiement, remboursement, commission, payout, taxe ou donnée Giftshop n’est modifié.',
      ]
        .filter(Boolean)
        .join('\n');

    const removeReceiver = () => {
      if (receiver) {
        receiver.remove();
        receiver = null;
      }
    };

    const openOffersTab = (packet: OfferHandoffPacketLite) => {
      window.dispatchEvent(
        new CustomEvent('commerce:open-tab', {
          detail: {
            tab: 'offersHub',
            source: 'forfy-offer-handoff-receiver-v22n10',
            draft: packet,
          },
        }),
      );
    };

    const openForfyCommerce = (packet: OfferHandoffPacketLite) => {
      setCommerceContext({
        source: 'forfy-offer-handoff-receiver-v22n10',
        businessId: null,
        businessName: packet.title || null,
        businessPlan: null,
        activeTab: 'offersHub',
        activeNav: 'offers',
        userRole: 'merchant',
      } as ForfyCommerceContext);

      setCommerceAssistantHandoff(false);
      setActiveMode('commerce');

      window.dispatchEvent(
        new CustomEvent('forfy:open', {
          detail: {
            mode: 'commerce',
            source: 'forfy-offer-handoff-receiver-v22n10',
            activeTab: 'offersHub',
            activeNav: 'offers',
          },
        }),
      );
    };

    const clearPacket = () => {
      try {
        window.localStorage?.removeItem(storageKey);
      } catch {
        // ignore unavailable storage
      }

      removeReceiver();
    };

    const applyShellStyle = (element: HTMLDivElement) => {
      element.style.position = 'fixed';
      element.style.right = '18px';
      element.style.bottom = 'calc(92px + env(safe-area-inset-bottom, 0px))';
      element.style.zIndex = '2147482997';
      element.style.width = 'min(430px, calc(100vw - 32px))';
      element.style.padding = '16px';
      element.style.borderRadius = '24px';
      element.style.background = 'linear-gradient(135deg, #ffffff 0%, #f7fffb 100%)';
      element.style.border = '1px solid rgba(16, 185, 129, 0.28)';
      element.style.boxShadow = '0 22px 56px rgba(15, 23, 42, 0.22)';
      element.style.color = '#0f172a';
      element.style.display = 'grid';
      element.style.gap = '12px';
      element.style.fontFamily = 'inherit';
    };

    const makeButton = (label: string, primary: boolean, onClick: () => void) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.addEventListener('click', onClick);
      button.style.minHeight = '42px';
      button.style.padding = '10px 13px';
      button.style.borderRadius = '999px';
      button.style.border = primary ? '1px solid #0b3b8f' : '1px solid rgba(148, 163, 184, 0.38)';
      button.style.background = primary ? 'linear-gradient(135deg, #0b3b8f, #114bd6)' : '#ffffff';
      button.style.color = primary ? '#ffffff' : '#0f172a';
      button.style.fontWeight = '900';
      button.style.cursor = 'pointer';
      button.style.touchAction = 'manipulation';
      button.style.webkitTapHighlightColor = 'transparent';
      return button;
    };

    const renderReceiver = () => {
      if (!isBusinessDashboard()) {
        removeReceiver();
        return;
      }

      const packet = readPacket();

      if (!packet) {
        removeReceiver();
        return;
      }

      if (!receiver) {
        receiver = document.createElement('div');
        receiver.id = 'forfy-commerce-offer-handoff-receiver';
        receiver.setAttribute('data-forfy-offer-handoff-receiver', 'v22n10');
        receiver.setAttribute('role', 'region');
        receiver.setAttribute('aria-label', 'Dossier Forfy prêt pour Offres');
        document.body.appendChild(receiver);
      }

      applyShellStyle(receiver);
      receiver.replaceChildren();

      const header = document.createElement('div');
      header.style.display = 'grid';
      header.style.gap = '3px';

      const eyebrow = document.createElement('span');
      eyebrow.textContent = 'DOSSIER FORFY PRÊT';
      eyebrow.style.color = '#047857';
      eyebrow.style.fontSize = '11px';
      eyebrow.style.fontWeight = '950';
      eyebrow.style.letterSpacing = '0.08em';

      const title = document.createElement('strong');
      title.textContent = packet.title || 'Nouvelle offre Forfeo';
      title.style.color = '#061a3a';
      title.style.fontSize = '16px';
      title.style.fontWeight = '950';

      const meta = document.createElement('span');
      meta.textContent = `${packet.category || 'Offre'} · ${packet.readinessScore ?? '—'}% · ${packet.readinessLabel || 'À valider'}`;
      meta.style.color = '#475569';
      meta.style.fontSize = '13px';
      meta.style.fontWeight = '800';

      header.append(eyebrow, title, meta);

      const guardrail = document.createElement('div');
      guardrail.textContent =
        'Brouillon local seulement. Validation humaine requise avant prix, publication ou disponibilité.';
      guardrail.style.padding = '10px 12px';
      guardrail.style.borderRadius = '16px';
      guardrail.style.background = '#fff7ed';
      guardrail.style.border = '1px solid rgba(249, 115, 22, 0.24)';
      guardrail.style.color = '#7c2d12';
      guardrail.style.fontSize = '12px';
      guardrail.style.fontWeight = '850';
      guardrail.style.lineHeight = '1.35';

      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.flexWrap = 'wrap';
      actions.style.gap = '8px';

      actions.append(
        makeButton('Ouvrir Offres', true, () => openOffersTab(packet)),
        makeButton('Ouvrir Forfy', false, () => openForfyCommerce(packet)),
        makeButton('Copier résumé', false, () => void copyText(buildSummary(packet))),
        makeButton('Effacer', false, clearPacket),
      );

      receiver.append(header, guardrail, actions);
    };

    const handlePrepared = (event: Event) => {
      const detail = (event as CustomEvent<OfferHandoffPacketLite>).detail;

      if (detail) {
        try {
          window.localStorage?.setItem(
            storageKey,
            JSON.stringify({
              ...detail,
              preparedAt: detail.preparedAt || new Date().toISOString(),
            }),
          );
        } catch (error) {
          console.warn('[Forfy Commerce] offer handoff storage skipped', error);
        }
      }

      renderReceiver();
    };

    const handleStorage = () => renderReceiver();
    const handleVisibility = () => renderReceiver();

    document.addEventListener('commerce:offer-draft-prepared', handlePrepared as EventListener);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleVisibility);
    window.addEventListener('popstate', handleVisibility);

    const refreshTimer = window.setInterval(renderReceiver, 1800);
    renderReceiver();

    return () => {
      document.removeEventListener('commerce:offer-draft-prepared', handlePrepared as EventListener);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleVisibility);
      window.removeEventListener('popstate', handleVisibility);
      window.clearInterval(refreshTimer);
      removeReceiver();
    };
  }, []);
  // FORFEO_COMMERCE_V22N10_OFFER_HANDOFF_RECEIVER_END

  // FORFEO_COMMERCE_V22N11_MOBILE_FULLSCREEN_COMMERCE_LOCK
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const styleId = 'forfy-commerce-mobile-fullscreen-lock-style';

    const isMobileViewport = () => {
      try {
        return window.matchMedia('(max-width: 860px), (pointer: coarse)').matches;
      } catch {
        return window.innerWidth <= 860;
      }
    };

    const applyState = () => {
      const shouldLock = activeMode === 'commerce' && isMobileViewport();

      document.documentElement.setAttribute(
        'data-forfy-commerce-mobile-fullscreen',
        shouldLock ? 'open' : 'closed',
      );

      document.body.setAttribute(
        'data-forfy-commerce-mobile-fullscreen',
        shouldLock ? 'open' : 'closed',
      );

      document.body.style.overscrollBehavior = shouldLock ? 'contain' : '';
    };

    const ensureStyle = () => {
      let style = document.getElementById(styleId) as HTMLStyleElement | null;

      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        style.setAttribute('data-forfy-commerce-mobile-fullscreen-lock', 'v22n11');
        document.head.appendChild(style);
      }

      style.textContent = `
        @media (max-width: 860px), (pointer: coarse) {
          html[data-forfy-commerce-mobile-fullscreen="open"],
          body[data-forfy-commerce-mobile-fullscreen="open"] {
            width: 100%;
            min-height: 100%;
            overflow-x: hidden !important;
            overscroll-behavior: contain;
            -webkit-text-size-adjust: 100%;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] [class*="assistant"],
          body[data-forfy-commerce-mobile-fullscreen="open"] [class*="Assistant"],
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-commerce-mobile-launcher] {
            -webkit-tap-highlight-color: transparent;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-cockpit,
          body[data-forfy-commerce-mobile-fullscreen="open"] [class*="forfy-commerce-cockpit"] {
            position: relative !important;
            width: 100% !important;
            max-width: 100% !important;
            min-height: 100dvh !important;
            height: auto !important;
            max-height: none !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-cockpit__shell,
          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-cockpit__body,
          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-cockpit__content,
          body[data-forfy-commerce-mobile-fullscreen="open"] [class*="forfy-commerce-cockpit__"] {
            max-width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-cockpit button,
          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-offer-assistant button,
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-offer-handoff] button,
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-launch-planner] button,
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-marketing-copy-pack] button {
            min-height: 46px !important;
            touch-action: manipulation;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] .forfy-commerce-offer-assistant,
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-offer-assistant],
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-marketing-copy-pack],
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-launch-planner],
          body[data-forfy-commerce-mobile-fullscreen="open"] [data-forfy-offer-handoff] {
            width: 100% !important;
            max-width: 100% !important;
            overflow-x: hidden !important;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] textarea,
          body[data-forfy-commerce-mobile-fullscreen="open"] input,
          body[data-forfy-commerce-mobile-fullscreen="open"] select {
            font-size: 16px !important;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] pre {
            max-width: 100% !important;
            overflow-x: auto !important;
            white-space: pre-wrap !important;
            word-break: break-word !important;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] #forfy-commerce-mobile-launcher {
            right: 16px !important;
            bottom: calc(16px + env(safe-area-inset-bottom, 0px)) !important;
            z-index: 2147483000 !important;
          }

          body[data-forfy-commerce-mobile-fullscreen="open"] #forfy-commerce-offer-handoff-receiver {
            left: 12px !important;
            right: 12px !important;
            bottom: calc(86px + env(safe-area-inset-bottom, 0px)) !important;
            width: auto !important;
            max-width: none !important;
          }
        }
      `;
    };

    ensureStyle();
    applyState();

    window.addEventListener('resize', applyState);
    window.addEventListener('orientationchange', applyState);

    const refreshTimer = window.setInterval(applyState, 1200);

    return () => {
      window.removeEventListener('resize', applyState);
      window.removeEventListener('orientationchange', applyState);
      window.clearInterval(refreshTimer);
      document.documentElement.removeAttribute('data-forfy-commerce-mobile-fullscreen');
      document.body.removeAttribute('data-forfy-commerce-mobile-fullscreen');
      document.body.style.overscrollBehavior = '';
    };
  }, [activeMode]);
  // FORFEO_COMMERCE_V22N11_MOBILE_FULLSCREEN_COMMERCE_LOCK_END





  // Initialiser les services et les écouteurs Vapi
  useEffect(() => {
    checkSupportStatus();
    loadTickets();
    setupSpeechRecognition();

    // ✅ ÉCOUTEURS VAPI (Gestion du WebRTC en temps réel)
    vapi.on('call-start', () => {
      console.log('Appel Vapi connecté');
      setCallView('connected');
      setCallDuration(0);
      // Démarrer le chronomètre
      timerRef.current = window.setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    });

    vapi.on('call-end', () => {
      console.log('Appel Vapi terminé');
      setCallView('menu');
      if (timerRef.current) clearInterval(timerRef.current);
    });

    vapi.on('error', (e) => {
      console.error("Erreur Vapi:", e);
      setCallView('voicemail');
      if (timerRef.current) clearInterval(timerRef.current);
    });

    return () => {
      SpeechRecognitionService.stopListening();
      vapi.removeAllListeners();
      if (timerRef.current) clearInterval(timerRef.current);
      vapi.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Défiler vers le bas dans le chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const setupSpeechRecognition = () => {
    if (!SpeechRecognitionService.isSupported()) {
      console.log('Reconnaissance vocale non supportée par ce navigateur');
      return;
    }

    SpeechRecognitionService.onResult((result) => {
      setTranscript(result.text);

      if (result.isFinal) {
        setMessage(result.text);
        SpeechRecognitionService.stopListening();
        setIsListening(false);
      }
    });

    SpeechRecognitionService.onError((error) => {
      console.error('Erreur reconnaissance vocale:', error);
      setIsListening(false);
      setTranscript('');
    });
  };

  const checkSupportStatus = () => {
    const isOpen = FORCE_SUPPORT_247 ? true : TicketService.isSupportOpen();
    const waitTime = TicketService.getEstimatedWaitTime();

    let nextOpening = '';
    if (!isOpen && !FORCE_SUPPORT_247) {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);

      nextOpening = tomorrow.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    setSupportStatus({ isOpen, waitTime, nextOpening });
  };

  const loadTickets = async () => {
    try {
      const userTickets = await TicketService.getUserTickets();
      setTickets(userTickets);
    } catch (error) {
      console.error('Erreur chargement tickets:', error);
    }
  };

  const toggleVoiceInput = async () => {
    if (isListening) {
      SpeechRecognitionService.stopListening();
      setIsListening(false);
      setTranscript('');
    } else {
      try {
        await SpeechRecognitionService.startListening();
        setIsListening(true);
        setTranscript('');
      } catch (error) {
        console.error('Erreur démarrage reconnaissance vocale:', error);
        const errorMessage: ChatMessage = {
          sender: 'assistant',
          text: "🎤 La reconnaissance vocale n'est pas disponible. Veuillez taper votre message.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setChatMessages((prev) => [...prev, errorMessage]);
      }
    }
  };

  // ✅ CONNEXION À L'API RAG SUPABASE (FORFY TEXTE)
  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userText = message;
    const userMessage: ChatMessage = {
      sender: 'user',
      text: userText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: isListening ? 'voice' : 'text'
    };

    const newMessages = [...chatMessages, userMessage];
    setChatMessages(newMessages);
    setMessage('');
    setTranscript('');
    setIsTyping(true);

    try {
      const response = await fetch('/api/support/ai/turn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: userText,
          lang: 'fr',
          wantAudio: false, 
        })
      });

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

      const data = await response.json();

      const assistantMessage: ChatMessage = {
        sender: 'assistant',
        text: data.replyText || "Désolé, je n'ai pas pu générer de réponse.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setChatMessages([...newMessages, assistantMessage]);
    } catch (error) {
      console.error("Erreur de connexion avec l'IA Forfy:", error);
      const errorMessage: ChatMessage = {
        sender: 'assistant',
        text: "Oups, j'ai un petit problème réseau. Pouvez-vous répéter votre question ou créer un ticket ?",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages([...newMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // ✅ DÉMARRER LE VRAI APPEL VAPI AVEC TON ID
  const startPhoneCall = async () => {
    if (!FORCE_SUPPORT_247 && !supportStatus.isOpen) {
      setCallView('voicemail');
      return;
    }

    try {
      setCallView('calling');
      // Lancement de l'agent Vapi avec ton Assistant ID
      await vapi.start('1bd44be2-b793-4f50-98b3-5dfa1bad9d44'); 
    } catch (error) {
      console.error('Erreur démarrage appel Vapi:', error);
      setCallView('voicemail');
    }
  };

  // ✅ RACCROCHER LE VRAI APPEL VAPI
  const endPhoneCall = () => {
    vapi.stop();
    setCallView('menu');
  };

  // ✅ GÉRER LE MUTE SUR VAPI
  const toggleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    vapi.setMuted(newMutedState);
  };

  const openCommerceTabFromCockpit = (tab: string) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('commerce:open-tab', {
        detail: { tab, source: 'forfy-commerce-cockpit' },
      }));
    }

    if (window.location.pathname !== '/business-dashboard') {
      navigate(`/business-dashboard?tab=${encodeURIComponent(tab)}`);
    }
  };

  const seedCommercePrompt = (prompt: string) => {
    setMessage(prompt);
    setChatMessages((prev) => [
      ...prev,
      {
        sender: 'assistant',
        text: "J’ai préparé une demande Forfy Commerce. Vérifie le texte, ajuste-le au besoin, puis appuie sur envoyer. Je reste assistif: aucune action financière ou transactionnelle ne sera exécutée.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    setCommerceAssistantHandoff(true);
    setActiveMode('support');
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'search': navigate('/explorer-plus'); break;
      case 'lastcall': navigate('/last-call'); break;
      case 'map': navigate('/carte'); break;
      case 'flights': navigate('/vols'); break;
      case 'club': navigate('/club'); break;
      case 'hotels': navigate('/explorer-plus?category=hotels'); break;
      case 'cart': {
        const cartButton = document.querySelector('[data-cart-toggle]') as HTMLElement;
        if (cartButton) cartButton.click();
        break;
      }
      case 'call': startPhoneCall(); break;
      case 'tickets': setActiveMode('tickets'); break;
      case 'voice': toggleVoiceInput(); break;
      default: break;
    }

    if (action !== 'call') {
      setActiveMode('closed');
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      const errorMessage: ChatMessage = {
        sender: 'assistant',
        text: "⚠️ Veuillez remplir tous les champs obligatoires pour créer un ticket.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages((prev) => [...prev, errorMessage]);
      return;
    }

    try {
      await TicketService.createTicket(newTicket);
      const confirmationMessage: ChatMessage = {
        sender: 'assistant',
        text: `✅ Ticket créé avec succès!\n• Sujet: ${newTicket.subject}\n• Priorité: ${newTicket.priority}\n• Référence: #${Math.floor(1000 + Math.random() * 9000)}\nNotre équipe vous répondra rapidement.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages((prev) => [...prev, confirmationMessage]);
      setNewTicket({ subject: '', description: '', category: 'general', priority: 'medium' });
      await loadTickets();
      setActiveMode('support');
    } catch (error) {
      console.error('Erreur création ticket:', error);
      const errorMessage: ChatMessage = {
        sender: 'assistant',
        text: "❌ Erreur lors de la création du ticket. Veuillez nous contacter par téléphone.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages((prev) => [...prev, errorMessage]);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const getTicketStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-500';
      case 'in_progress': return 'bg-yellow-500';
      case 'resolved': return 'bg-green-500';
      case 'closed': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-400';
    }
  };

  // 1️⃣ Rendu Forfy (Hub de navigation)
  const renderForfyContent = () => (
    <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-5 text-white w-full max-w-xs md:max-w-sm shadow-2xl border border-slate-700">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
            <FaRobot className="text-xl" />
          </div>
          <div>
            <h3 className="font-black text-sm md:text-base">Forfy Smart Hub</h3>
            <p className="text-xs text-blue-200">Assistant de voyage IA</p>
          </div>
        </div>
        <button onClick={() => setActiveMode('closed')} className="text-slate-300 hover:text-white p-1">
          <FaTimes />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <button onClick={() => handleQuickAction('search')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaSearch className="text-lg mb-2 text-blue-300 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Recherche</span>
        </button>
        <button onClick={() => handleQuickAction('lastcall')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaFire className="text-lg mb-2 text-yellow-300 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Last Call</span>
        </button>
        <button onClick={() => handleQuickAction('map')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaMapMarkerAlt className="text-lg mb-2 text-green-300 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Carte</span>
        </button>
        <button onClick={() => handleQuickAction('hotels')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaHotel className="text-lg mb-2 text-purple-300 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Hôtels</span>
        </button>
        <button onClick={() => handleQuickAction('flights')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaPlane className="text-lg mb-2 text-cyan-300 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Vols</span>
        </button>
        <button onClick={() => handleQuickAction('club')} className="bg-white/10 hover:bg-white/20 p-4 rounded-xl transition flex flex-col items-center justify-center group">
          <FaStar className="text-lg mb-2 text-yellow-400 group-hover:scale-110 transition" />
          <span className="text-xs font-bold">Club</span>
        </button>
      </div>

      <div className="bg-black/20 p-4 rounded-xl">
        <p className="text-xs text-blue-200 mb-2">🤖 Planifiez votre voyage avec Forfy</p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Ex: Week-end à Montréal..."
            className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs outline-none placeholder:text-slate-400"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                navigate('/explorer-plus?q=' + (e.target as HTMLInputElement).value);
              }
            }}
          />
          <button className="bg-blue-500 hover:bg-blue-600 px-4 rounded-lg font-bold text-xs transition flex items-center gap-1">
            <FaSearch size={10} /> GO
          </button>
        </div>
      </div>
    </div>
  );

  // 2️⃣ Rendu Forfy Support (Le Chat RAG)
  const renderSupportContent = () => {
    const isOpenNow = FORCE_SUPPORT_247 ? true : supportStatus.isOpen;
    const supportShellTitle = commerceAssistantHandoff ? 'Forfy Commerce Assistant' : 'Forfy Support';
    const supportShellSubtitle = commerceAssistantHandoff
      ? 'Brouillon marchand à valider avant envoi'
      : `Aide humaine et IA ${FORCE_SUPPORT_247 ? '24/7' : ''}`;

    if (commerceAssistantHandoff) {
      return (
        <div className="forfy-commerce-editor-shell forfy-commerce-editor-shell--v22k5 forfy-commerce-editor-shell--v22k6 forfy-commerce-editor-shell--v22k7">
          <div className="forfy-commerce-editor-shell__compact-head">
            <div className="forfy-commerce-editor-shell__compact-brand">
              <div className="forfy-commerce-editor-shell__compact-avatar"><FaRobot /></div>
              <div>
                <p>Mode éditeur Forfy Commerce</p>
                <h3>Brouillon Forfy prêt à valider</h3>
                <span>{commerceContext?.businessName || 'Commerce Forfeo'} · {commerceContext?.userRole || 'marchand'} · validation humaine requise</span>
              </div>
            </div>
            <div className="forfy-commerce-editor-shell__compact-actions">
              <button
                type="button"
                onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('commerce'); }}
                className="forfy-commerce-editor-shell__return forfy-commerce-editor-shell__return--v22k6"
              >
                <FaReply size={11} /> Retour cockpit
              </button>
              <button
                type="button"
                onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('closed'); }}
                className="forfy-commerce-editor-shell__close forfy-commerce-editor-shell__close--v22k6"
                aria-label="Fermer Forfy Commerce Assistant"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="forfy-commerce-editor-shell__guardline">
            <strong>Forfy reste assistif.</strong>
            <span>Aucune action financière, aucun paiement, prix, remboursement, commission, payout, taxe ou référence Stripe ne sera exécuté.</span>
          </div>

          <div className="forfy-commerce-editor-shell__editor-card forfy-commerce-editor-shell__editor-card--v22k6">
            <label className="forfy-commerce-editor-shell__editor-label" htmlFor="forfy-commerce-editor-draft">
              Brouillon à relire avant envoi
            </label>
            <div className="forfy-commerce-editor-shell__editor-row">
              <textarea
                id="forfy-commerce-editor-draft"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    handleSendMessage();
                  }
                }}
                placeholder="Valide ou ajuste le brouillon Forfy Commerce avant envoi..."
                rows={16}
                className="forfy-commerce-editor-shell__textarea forfy-commerce-editor-shell__textarea--v22k6"
              />
              <div className="forfy-commerce-editor-shell__sendbar forfy-commerce-editor-shell__sendbar--v22k6">
                <button
                  type="button"
                  onClick={toggleVoiceInput}
                  className={`forfy-commerce-editor-shell__round ${isListening ? 'forfy-commerce-editor-shell__round--danger' : ''}`}
                  aria-label={isListening ? 'Arrêter la dictée' : 'Dicter le brouillon'}
                >
                  {isListening ? <FaStop /> : <FaMicrophone />}<span className="forfy-commerce-editor-shell__dictation-label--v22k7">{isListening ? 'Arrêter' : 'Dicter'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleSendMessage}
                  className="forfy-commerce-editor-shell__send forfy-commerce-editor-shell__send--v22k7"
                  disabled={!message.trim() || isTyping}
                >
                  <FaPaperPlane /> Envoyer
                </button>
              </div>
            </div>
          </div>

          {isListening && (
            <div className="forfy-commerce-editor-shell__listening">
              <strong>Dictée active</strong>
              <span>{transcript || 'Parle maintenant. Le texte apparaîtra dans le brouillon.'}</span>
            </div>
          )}

          <div className="forfy-commerce-editor-shell__quick-actions forfy-commerce-editor-shell__quick-actions--v22k6">
            <button type="button" onClick={() => setMessage("J'ai un problème de réservation")}>📅 Problème réservation</button>
            <button type="button" onClick={() => setMessage('Comment fonctionnent les points fidélité?')}>⭐ Points fidélité</button>
            <button type="button" onClick={() => handleQuickAction('call')}>👨‍💼 Appeler un agent</button>
            <button type="button" onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('tickets'); }}>🎫 Mes tickets</button>
          </div>
        </div>
      );
    }

    return (
      <div className={`forfy-support-shell forfy-support-shell--v22k2 forfy-support-shell--v22k3 ${commerceAssistantHandoff ? 'forfy-support-shell--commerce-handoff' : ''} bg-gradient-to-br from-blue-900 to-indigo-900 rounded-3xl p-5 text-white w-full max-w-xs md:max-w-sm shadow-2xl border border-blue-700`}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-xl flex items-center justify-center">
              <FaHeadset className="text-xl" />
            </div>
            <div>
              <h3 className="font-black text-sm md:text-base">{supportShellTitle}</h3>
              <p className="text-xs text-blue-200">{supportShellSubtitle}</p>
            </div>
          </div>
          <div className="forfy-support-shell__header-actions flex items-center gap-2">
            {commerceAssistantHandoff && (
              <button
                type="button"
                onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('commerce'); }}
                className="forfy-support-shell__return px-3 py-2 text-xs font-black rounded-full"
              >
                <FaReply size={10} /> Retour cockpit
              </button>
            )}
            <button onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('closed'); }} className="text-slate-300 hover:text-white p-1" aria-label="Fermer Forfy">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Status */}
        <div className={`forfy-support-shell__status mb-4 p-3 rounded-xl ${isOpenNow ? 'bg-green-500/20 border border-green-500/30' : 'bg-yellow-500/20 border border-yellow-500/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaClock className={`text-sm ${isOpenNow ? 'text-green-400' : 'text-yellow-400'}`} />
              <span className="text-xs font-bold">{isOpenNow ? 'Assistance active' : 'Assistance limitée'}</span>
            </div>
            <button onClick={() => { setCommerceAssistantHandoff(false); handleQuickAction('tickets'); }} className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded-lg transition flex items-center gap-1">
              <FaTicketAlt /> Tickets
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="forfy-support-shell__messages h-48 md:h-56 overflow-y-auto mb-4 space-y-3 p-1">
          {chatMessages.map((msg, index) => (
            <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`forfy-support-shell__bubble max-w-[85%] p-3 rounded-2xl ${msg.sender === 'user' ? 'bg-blue-500 rounded-br-none' : 'bg-white/10 rounded-bl-none'}`}>
                <div className="flex items-start gap-2">
                  {msg.type === 'voice' && <FaMicrophone className="text-xs mt-0.5 text-blue-300" />}
                  <p className="text-xs md:text-sm whitespace-pre-wrap">{msg.text}</p>
                </div>
                <span className="text-xs opacity-70 block mt-1 text-right">
                  {msg.sender === 'user' ? 'Vous' : 'Forfy'} • {msg.timestamp}
                </span>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="max-w-[85%] p-3 rounded-2xl bg-white/10 rounded-bl-none">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-300 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  <span className="text-xs ml-2">Forfy réfléchit...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Voice Feedback */}
        {isListening && (
          <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FaMicrophone className="text-purple-400 animate-pulse" />
                <span className="text-xs font-bold text-purple-300">🎤 En écoute...</span>
              </div>
              <button onClick={toggleVoiceInput} className="text-xs bg-red-500 hover:bg-red-600 px-2 py-1 rounded-lg transition flex items-center gap-1">
                <FaStop /> Arrêter
              </button>
            </div>
            {transcript && <p className="text-xs text-white bg-black/30 p-2 rounded-lg mt-2">"{transcript}"</p>}
          </div>
        )}

        {/* Input Text */}
        <div className="forfy-support-shell__composer flex gap-2 mb-4">
          {commerceAssistantHandoff ? (
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              placeholder="Valide ou ajuste le brouillon Forfy Commerce avant envoi..."
              rows={12}
              className="forfy-support-shell__commerce-textarea flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs md:text-sm outline-none placeholder:text-slate-400 resize-none"
            />
          ) : (
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={isListening ? 'Parlez maintenant...' : 'Posez une question...'}
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs md:text-sm outline-none placeholder:text-slate-400"
            />
          )}
          <button
            onClick={toggleVoiceInput}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition ${isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-purple-500 hover:bg-purple-600'}`}
          >
            {isListening ? <FaStop /> : <FaMicrophone />}
          </button>
          <button
            onClick={handleSendMessage}
            className="bg-blue-500 hover:bg-blue-600 w-10 h-10 rounded-lg flex items-center justify-center transition disabled:opacity-50"
            disabled={!message.trim() || isTyping}
          >
            <FaPaperPlane />
          </button>
        </div>

        {/* Quick Actions Support */}
        <div className="forfy-support-shell__quick-actions grid grid-cols-2 gap-2 mb-4">
          <button onClick={() => setMessage("J'ai un problème de réservation")} className="bg-white/5 hover:bg-white/10 p-2 rounded-lg text-xs text-left transition flex items-center gap-2">
            <span className="text-blue-300">📅</span><span>Problème réservation</span>
          </button>
          <button onClick={() => setMessage('Comment fonctionnent les points fidélité?')} className="bg-white/5 hover:bg-white/10 p-2 rounded-lg text-xs text-left transition flex items-center gap-2">
            <span className="text-yellow-300">⭐</span><span>Points fidélité</span>
          </button>
          <button onClick={() => handleQuickAction('call')} className="bg-white/5 hover:bg-white/10 p-2 rounded-lg text-xs text-left transition flex items-center gap-2">
            <span className="text-green-300">👨‍💼</span><span>Appeler un agent</span>
          </button>
          <button onClick={() => { setCommerceAssistantHandoff(false); setActiveMode('tickets'); }} className="bg-white/5 hover:bg-white/10 p-2 rounded-lg text-xs text-left transition flex items-center gap-2">
            <span className="text-purple-300">🎫</span><span>Mes tickets</span>
          </button>
        </div>
      </div>
    );
  };

  // 3️⃣ Rendu Tickets
  const renderTicketsContent = () => (
    <div className="forfy-ticket-shell forfy-ticket-shell--v22k2 bg-gradient-to-br from-indigo-900 to-purple-900 rounded-3xl p-5 text-white w-full max-w-xs md:max-w-sm shadow-2xl border border-purple-700">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <FaTicketAlt className="text-xl" />
          </div>
          <div>
            <h3 className="font-black text-sm md:text-base">Mes Tickets</h3>
            <p className="text-xs text-purple-200">Support & Assistance</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveMode('support')} className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded-lg transition flex items-center gap-1">
            <FaReply size={10} />
          </button>
          <button onClick={() => setActiveMode('closed')} className="text-slate-300 hover:text-white p-1">
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="forfy-ticket-shell__form mb-5 bg-black/30 p-4 rounded-2xl">
        <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
          <FaFileAlt /> Nouveau ticket
        </h4>
        <div className="space-y-3">
          <input
            type="text"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            placeholder="Sujet du ticket"
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs outline-none"
          />
          <textarea
            value={newTicket.description}
            onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
            placeholder="Décrivez votre problème..."
            rows={3}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs outline-none resize-none"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-purple-300 block mb-1">Catégorie</label>
              <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value as any })} className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-xs outline-none">
                <option value="general">Général</option>
                <option value="reservation">Réservation</option>
                <option value="technical">Technique</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-purple-300 block mb-1">Priorité</label>
              <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value as any })} className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-xs outline-none">
                <option value="low">Basse</option>
                <option value="medium">Moyenne</option>
                <option value="high">Haute</option>
              </select>
            </div>
          </div>
          <button onClick={createTicket} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-2 rounded-lg font-bold text-xs transition flex items-center justify-center gap-2">
            <FaPaperPlane /> Créer le ticket
          </button>
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto">
        <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><FaClock /> Récents</h4>
        {tickets.length === 0 ? (
          <div className="text-center py-8"><FaTicketAlt className="text-3xl text-white/30 mx-auto mb-2" /><p className="text-xs text-white/50">Aucun ticket</p></div>
        ) : (
          <div className="space-y-2">
            {tickets.slice(0, 5).map((t) => (
              <div key={t.id} className="bg-white/5 hover:bg-white/10 p-3 rounded-xl transition">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-bold truncate">{t.subject}</span>
                  <div className="flex gap-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${getTicketStatusColor(t.status)}`}>{t.status}</span>
                  </div>
                </div>
                <p className="text-xs text-white/70 truncate mb-2">{t.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // 4️⃣ Modal d'appel (WebRTC Vapi)
  const renderPhoneModal = () => (
    <AnimatePresence>
      {callView !== 'menu' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setCallView('menu')}>
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-3xl p-6 text-white max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-black text-lg">Appel vocal</h3>
              <button onClick={() => setCallView('menu')} className="text-slate-300 hover:text-white"><FaTimes /></button>
            </div>
            
            {callView === 'voicemail' ? (
              <div className="text-center py-8">
                <FaBusinessTime className="text-5xl text-yellow-400 mx-auto mb-4" />
                <h4 className="text-xl font-black mb-2">Impossible d'appeler</h4>
                <p className="text-sm text-blue-200 mb-6">Le support vocal est indisponible actuellement.</p>
                <button onClick={() => { setActiveMode('support'); setCallView('menu'); }} className="w-full bg-white/10 hover:bg-white/20 py-3 rounded-xl font-bold">Passer au chat</button>
              </div>
            ) : callView === 'calling' ? (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-blue-500 flex items-center justify-center animate-pulse"><FaUser className="text-4xl" /></div>
                <h4 className="text-xl font-black mb-4">Connexion en cours...</h4>
                <button onClick={endPhoneCall} className="bg-red-500 px-6 py-3 rounded-xl font-bold"><FaPhoneSlash className="inline mr-2"/> Annuler</button>
              </div>
            ) : callView === 'connected' ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-24 h-24 mx-auto mb-4 relative">
                    <div className="w-full h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                      <FaUser className="text-3xl" />
                    </div>
                    {!isMuted && (
                      <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                        <FaMicrophone className="text-white text-sm" />
                      </div>
                    )}
                  </div>

                  <h4 className="text-xl font-black mb-2">Agent Forfy</h4>
                  <p className="text-blue-200 mb-4">Assistance vocale IA</p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-400">
                      <FaCheckCircle />
                      <span>Connecté</span>
                    </div>
                    <div className="text-3xl font-black">{formatDuration(callDuration)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-8">
                  <button
                    onClick={toggleMute}
                    className={`p-4 rounded-xl transition flex flex-col items-center justify-center ${
                      isMuted ? 'bg-red-500/20 border border-red-500/30 text-red-400' : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {isMuted ? <FaVolumeMute className="text-2xl mb-2" /> : <FaVolumeUp className="text-2xl mb-2" />}
                    <span className="text-sm font-bold">{isMuted ? 'Micro coupé' : 'Sourdine'}</span>
                  </button>

                  <button onClick={endPhoneCall} className="bg-red-500 hover:bg-red-600 p-4 rounded-xl transition flex flex-col items-center justify-center">
                    <FaPhoneSlash className="text-2xl mb-2" />
                    <span className="text-sm font-bold">Raccrocher</span>
                  </button>
                </div>
              </>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <AnimatePresence>
        {activeMode !== 'closed' && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            data-forfy-assistant-mode={activeMode}
            className={`forfy-assistant-drawer-shell forfy-assistant-drawer-shell--v22k2 ${activeMode === 'commerce' ? 'forfy-assistant-drawer-shell--merchant-panel' : ''} fixed inset-x-3 top-[4.85rem] z-[9997] max-h-[calc(100vh-6rem)] overflow-y-auto overflow-x-hidden overscroll-contain md:inset-x-auto md:right-6 md:top-20 ${activeMode === 'commerce' || commerceAssistantHandoff ? 'md:w-[860px] lg:w-[900px] xl:w-[960px] 2xl:w-[1040px] max-w-[calc(100vw-2rem)]' : 'md:w-[390px]'}`}
          >
            {activeMode === 'commerce'
              ? (
                <ForfyCommerceRuntimeBoundary
                  onClose={() => setActiveMode('closed')}
                  onReturnToSupport={() => setActiveMode('support')}
                >
                  {((import.meta as any).env?.VITE_FORFY_MERCHANT_PANEL_V1 ?? 'true') !== 'false' ? (
                    <ForfyMerchantPanel
                      context={commerceContext}
                      onClose={() => setActiveMode('closed')}
                      onOpenTab={openCommerceTabFromCockpit}
                      onSeedPrompt={seedCommercePrompt}
                    />
                  ) : (
                    <ForfyCommerceCockpit
                      context={commerceContext}
                      onClose={() => setActiveMode('closed')}
                      onOpenTab={openCommerceTabFromCockpit}
                      onSeedPrompt={seedCommercePrompt}
                    />
                  )}
                </ForfyCommerceRuntimeBoundary>
              )
              : activeMode === 'forfy'
                ? renderForfyContent()
                : activeMode === 'support'
                  ? renderSupportContent()
                  : activeMode === 'tickets'
                    ? renderTicketsContent()
                    : null}
          </motion.div>
        )}
      </AnimatePresence>
      {renderPhoneModal()}
    </>
  );
};

export default SmartAssistantWidget;
